import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'

import { ContextGroupMode } from './types'
import { ContextRow } from './useTaskDashboardData'
import useLocaleText from 'components/useLocaleText'
import { getOwnVisibilityGroupIds } from 'utils/taskAnalytics'

// Keyed by the 5 task statuses only — a `ContextRow.status` can never be SKIPPED /
// APPROVED_WITH_RESERVATION / REVIEWED_BY_AI anymore (see utils/taskStatusModel).
const COMPLETION_WEIGHTS: Partial<Record<number, number>> = {
  [keys.TASK_STATUSES.DONE.value]: 1,
  [keys.TASK_STATUSES.APPROVED.value]: 1,
  [keys.TASK_STATUSES.DISAPPROVED.value]: 0.5,
  [keys.TASK_STATUSES.INPROGRESS.value]: 0.25,
  [keys.TASK_STATUSES.TODO.value]: 0,
}

const completionScore = (contexts: ContextRow[]) => {
  if (contexts.length === 0) return 0
  return contexts.reduce((sum, c) => sum + (COMPLETION_WEIGHTS[c.status] ?? 0), 0) / contexts.length
}

const sortByCompletion = <T extends { contexts: ContextRow[] }>(sections: T[]): T[] =>
  [...sections].sort((a, b) => completionScore(b.contexts) - completionScore(a.contexts))

export type ContextGroupSection = {
  key: string
  label: string
  color?: string
  contexts: ContextRow[]
}

const addToSection = (
  map: Map<string, { label: string; color?: string; contexts: ContextRow[] }>,
  key: string,
  label: string,
  context: ContextRow,
  color?: string
) => {
  if (!map.has(key)) map.set(key, { label, color, contexts: [] })
  const section = map.get(key)!
  if (!section.contexts.some((c) => c.contextId === context.contextId)) {
    section.contexts.push(context)
  }
}

export const useContextGrouping = (
  contexts: ContextRow[],
  mode: ContextGroupMode,
  auditors: string[] = []
): ContextGroupSection[] | null => {
  const groupsObject = useSelector((state: RootState) => state.config.config.groups)
  const retailTags = useSelector((state: RootState) => state.config.config.tags)
  const retailUsersObject = useSelector((state: RootState) => state.config.config.retailUsersObject)
  const mappedGroupsByUsersByFeature = useSelector(
    (state: RootState) => state.config.config.mappedGroupsByUsersByFeature
  )
  const mappedGroupIdsByUsers = useSelector((state: RootState) => state.config.config.mappedGroupIdsByUsers)
  const myUid = useSelector((state: RootState) => state.auth.uid)

  const untaggedLabel = useLocaleText('untagged')

  return useMemo(() => {
    if (mode === 'all') return null

    if (mode === 'groups') {
      const sectionMap = new Map<string, { label: string; color?: string; contexts: ContextRow[] }>()

      // Build uid→groupIds map once — O(groups×uids) — instead of scanning per context O(contexts×groups)
      const uidToGroupIds = new Map<string, string[]>()
      Object.entries(groupsObject || {}).forEach(([groupId, group]) => {
        group.uids?.forEach((uid: string) => {
          const existing = uidToGroupIds.get(uid) || []
          existing.push(groupId)
          uidToGroupIds.set(uid, existing)
        })
      })

      contexts.forEach((context) => {
        const isUser = !!retailUsersObject?.[context.contextId]

        if (!isUser) {
          const group = groupsObject?.[context.contextId]
          const parentId = group?.parent
          if (parentId && groupsObject?.[parentId]) {
            addToSection(sectionMap, parentId, groupsObject[parentId].name, context)
          } else {
            addToSection(sectionMap, '__root__', 'Top Level', context)
          }
        } else {
          const groupIds = uidToGroupIds.get(context.contextId) || []
          if (groupIds.length === 0) {
            addToSection(sectionMap, '__ungrouped__', 'Ungrouped', context)
          } else {
            groupIds.forEach((groupId) => {
              const group = groupsObject?.[groupId]
              if (group) addToSection(sectionMap, groupId, group.name, context)
            })
          }
        }
      })

      return sortByCompletion(
        Array.from(sectionMap.entries()).map(([key, { label, color, contexts: ctxs }]) => ({
          key,
          label,
          color,
          contexts: ctxs,
        }))
      )
    }

    if (mode === 'tags') {
      const sectionMap = new Map<string, { label: string; color?: string; contexts: ContextRow[] }>()
      const untagged: ContextRow[] = []

      contexts.forEach((context) => {
        const isUser = !!retailUsersObject?.[context.contextId]
        const matchingTags = (retailTags || []).filter((tag) =>
          isUser ? tag.users?.includes(context.contextId) : tag.groups?.includes(context.contextId)
        )

        if (matchingTags.length === 0) {
          untagged.push(context)
        } else {
          matchingTags.forEach((tag) => {
            addToSection(sectionMap, tag.sk, tag.name, context, tag.tag_color)
          })
        }
      })

      const sections = sortByCompletion(
        Array.from(sectionMap.entries()).map(([key, { label, color, contexts: ctxs }]) => ({
          key,
          label,
          color,
          contexts: ctxs,
        }))
      )
      if (untagged.length > 0)
        sections.push({ key: '__untagged__', label: untaggedLabel, color: undefined, contexts: untagged })

      return sections
    }

    if (mode === 'auditors') {
      const sectionMap = new Map<string, { label: string; color?: string; contexts: ContextRow[] }>()

      // The viewer's own visibility area — computed once, only needed for auditors that fall
      // back to org-tree resolution below.
      let viewerVisibilityGroupIds: Set<string> | null = null

      auditors.forEach((auditorUid) => {
        const vmGroupIds = mappedGroupsByUsersByFeature?.VM?.[auditorUid] ?? []
        const hasVmMapping = vmGroupIds.length > 0
        const auditor = retailUsersObject?.[auditorUid]
        const auditorName = auditor
          ? `${auditor.first_name || ''} ${auditor.last_name || ''}`.trim() || auditorUid
          : auditorUid

        // Auditors explicitly authorized via mappedGroupsByUsersByFeature.VM are trusted as-is —
        // that mapping is the backend-vetted source of truth for which groups they audit.
        let auditorGroupIdsSet: Set<string>
        if (hasVmMapping) {
          auditorGroupIdsSet = new Set(vmGroupIds)
        } else {
          // Manually assigned auditors (present on the task but absent from the VM feature
          // mapping) don't have an explicit group grant, so derive their area from the org tree:
          // their own group(s) plus everything below. Clipped to the current viewer's own
          // visibility area so a viewer never sees contexts outside their own permitted subtree
          // through another auditor's (possibly broader) reach.
          if (!viewerVisibilityGroupIds) {
            viewerVisibilityGroupIds = myUid
              ? getOwnVisibilityGroupIds(myUid, mappedGroupIdsByUsers, groupsObject)
              : new Set<string>()
          }
          const auditorOwnGroupIds = getOwnVisibilityGroupIds(auditorUid, mappedGroupIdsByUsers, groupsObject)
          auditorGroupIdsSet = new Set(Array.from(auditorOwnGroupIds).filter((id) => viewerVisibilityGroupIds!.has(id)))
        }

        // Always create the section — even with zero matched contexts — so every auditor
        // assigned on the task appears in the list. The UI shows a real "0" and disables
        // the card rather than silently hiding auditors the viewer/backend can't resolve.
        if (!sectionMap.has(auditorUid)) sectionMap.set(auditorUid, { label: auditorName, contexts: [] })

        contexts.forEach((context) => {
          const contextGroupIds = retailUsersObject?.[context.contextId]
            ? mappedGroupIdsByUsers?.[context.contextId] ?? []
            : [context.contextId]
          const isAuditorContext = contextGroupIds.some((groupId) => auditorGroupIdsSet.has(groupId))

          if (!isAuditorContext) {
            return
          }

          addToSection(sectionMap, auditorUid, auditorName, context)
        })
      })

      return sortByCompletion(
        Array.from(sectionMap.entries()).map(([key, { label, color, contexts: ctxs }]) => ({
          key,
          label,
          color,
          contexts: ctxs,
        }))
      )
    }

    return null
  }, [
    mode,
    groupsObject,
    contexts,
    retailUsersObject,
    untaggedLabel,
    retailTags,
    auditors,
    mappedGroupsByUsersByFeature,
    mappedGroupIdsByUsers,
    myUid,
  ])
}
